import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated'
import { FAIcon } from '@/components'
import { format } from '@/utils'
import { ActionLogsList } from '@/modules/timesheets/components/action-logs-list'
import type { ActionFieldRecord } from '@/modules/timesheets/absence-details/absence-logs/util'
import { WORK_PERIOD_LOGS } from '../queries'
import type { WorkPeriodLogNode } from '../types.graphql'

interface ViewWorkPeriodLogsModalProps {
  onClose: PromptResolve<boolean | null>
  workPeriodId: string
}

interface WorkPeriodLogTranslations {
  actionLog: string
  noActivity: string
  close: string
  loading: string
  system: string
  start: string
  end: string
  approvedByUser: string
  userComment: string
  approvedByManager: string
  managerComment: string
  salarySetting: string
  leaderGroup: string
  calendarItem: string
  dayLength: string
  yes: string
  no: string
  workPeriod: string
  sourceActivity: string
  sourceTimeEntry: string
  sourceLabel: string
  activityPaid: string
  paid: string
  unpaid: string
  timeEntryStart: string
  timeEntryEnd: string
  timeEntryDepartment: string
  timeEntryFacility: string
  hoursAbbrev: string
  minutesAbbrev: string
  logSummary: (values: { user: string; action: string; target: string }) => string
  extensionExtended: (values: { amount: string; source: string }) => string
  extensionShortened: (values: { amount: string; source: string }) => string
  extensionCreated: (values: { amount: string; source: string }) => string
  logActionCreated: string
  logActionUpdated: string
  logActionDeleted: string
  logActionExtended: string
  logShowDetails: string
  logHideDetails: string
  logTabNew: string
  logTabOld: string
  lastLocked: (values: { date: string }) => string
  afterLock: string
}

// Field display order in the expanded detail view.
const FIELD_ORDER: Record<string, number> = {
  source: 5,
  datetime_start: 10,
  datetime_end: 11,
  extended_by: 12,
  approved_by_user: 20,
  user_comment: 21,
  approved_by_manager: 30,
  manager_comment: 31,
  salary_setting_id: 40,
  leader_group_id: 50,
  calendar_item_id: 60,
  activity_paid: 70,
  time_entry_datetime_start: 80,
  time_entry_datetime_end: 81,
  time_entry_department: 82,
  time_entry_facility: 83,
}

type ExtendedBy = {
  source_type?: unknown
  label?: unknown
  side?: unknown
  delta_seconds?: unknown
}

function getOrderedFieldEntries(fields: ActionFieldRecord) {
  return Object.entries(fields).sort(([fieldA], [fieldB]) => {
    const rankA = FIELD_ORDER[fieldA] ?? 999
    const rankB = FIELD_ORDER[fieldB] ?? 999
    if (rankA !== rankB) return rankA - rankB
    return fieldA.localeCompare(fieldB)
  })
}

function normalizeAction(value: string): string {
  return value.toUpperCase()
}

function formatBooleanValue(
  raw: unknown,
  yesLabel: string,
  noLabel: string
): string {
  if (raw === true || raw === 'true') return yesLabel
  if (raw === false || raw === 'false') return noLabel
  return String(raw)
}

function formatDuration(
  totalSeconds: number,
  translations: WorkPeriodLogTranslations
): string {
  const seconds = Math.abs(Math.round(totalSeconds))
  const totalMinutes = Math.round(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const h = translations.hoursAbbrev
  const m = translations.minutesAbbrev
  if (hours && minutes) return `${hours}${h} ${minutes}${m}`
  if (hours) return `${hours}${h}`
  return `${minutes}${m}`
}

function formatExtendedBy(
  raw: unknown,
  translations: WorkPeriodLogTranslations
): string {
  if (!raw || typeof raw !== 'object') return '-'

  const value = raw as ExtendedBy
  const deltaSeconds =
    typeof value.delta_seconds === 'number'
      ? value.delta_seconds
      : Number.parseFloat(String(value.delta_seconds))
  if (!Number.isFinite(deltaSeconds)) return '-'

  const sourceType = String(value.source_type ?? '')
  const sourceBase =
    sourceType === 'activity'
      ? translations.sourceActivity
      : translations.sourceTimeEntry
  const label =
    typeof value.label === 'string' && value.label
      ? `${sourceBase}: ${value.label}`
      : sourceBase

  const amount = formatDuration(deltaSeconds, translations)

  if (value.side === 'created') {
    return translations.extensionCreated({ amount, source: label })
  }
  if (deltaSeconds < 0) {
    return translations.extensionShortened({ amount, source: label })
  }
  return translations.extensionExtended({ amount, source: label })
}

function formatSource(
  raw: unknown,
  translations: WorkPeriodLogTranslations
): string {
  if (!raw || typeof raw !== 'object') return '-'
  const value = raw as { source_type?: unknown; label?: unknown }
  const base =
    value.source_type === 'activity'
      ? translations.sourceActivity
      : translations.sourceTimeEntry
  return typeof value.label === 'string' && value.label
    ? `${base}: ${value.label}`
    : base
}

function formatActionFieldValue(
  field: string,
  raw: unknown,
  translations: WorkPeriodLogTranslations
): string {
  if (field === 'extended_by') return formatExtendedBy(raw, translations)
  if (field === 'source') return formatSource(raw, translations)

  if (raw == null || raw === '') return '-'

  if (
    (field === 'datetime_start' ||
      field === 'datetime_end' ||
      field === 'time_entry_datetime_start' ||
      field === 'time_entry_datetime_end') &&
    typeof raw === 'string'
  ) {
    const date = new Date(raw)
    if (!Number.isNaN(date.getTime())) return format(date, 'dd.MM.yyyy HH:mm')
  }

  if (field === 'approved_by_user' || field === 'approved_by_manager') {
    return formatBooleanValue(raw, translations.yes, translations.no)
  }

  if (field === 'activity_paid') {
    return formatBooleanValue(raw, translations.paid, translations.unpaid)
  }

  if (field === 'time_entry_department' || field === 'time_entry_facility') {
    if (raw && typeof raw === 'object') {
      const relation = raw as { name?: unknown }
      return typeof relation.name === 'string' && relation.name
        ? relation.name
        : '-'
    }
    return '-'
  }

  if (field === 'salary_setting_id') {
    // New logs store { id, name }; older logs stored a bare id.
    if (raw && typeof raw === 'object') {
      const setting = raw as { id?: unknown; name?: unknown }
      if (typeof setting.name === 'string' && setting.name) return setting.name
      if (setting.id != null) return `#${setting.id}`
      return '-'
    }
    return `#${raw}`
  }

  if (field === 'leader_group_id' || field === 'calendar_item_id') {
    return `#${raw}`
  }

  return String(raw)
}

function getActionFieldLabel(
  field: string,
  translations: WorkPeriodLogTranslations
) {
  if (field === 'datetime_start') return translations.start
  if (field === 'datetime_end') return translations.end
  if (field === 'extended_by') return translations.dayLength
  if (field === 'approved_by_user') return translations.approvedByUser
  if (field === 'user_comment') return translations.userComment
  if (field === 'approved_by_manager') return translations.approvedByManager
  if (field === 'manager_comment') return translations.managerComment
  if (field === 'salary_setting_id') return translations.salarySetting
  if (field === 'leader_group_id') return translations.leaderGroup
  if (field === 'calendar_item_id') return translations.calendarItem
  if (field === 'source') return translations.sourceLabel
  if (field === 'activity_paid') return translations.activityPaid
  if (field === 'time_entry_datetime_start') return translations.timeEntryStart
  if (field === 'time_entry_datetime_end') return translations.timeEntryEnd
  if (field === 'time_entry_department') return translations.timeEntryDepartment
  if (field === 'time_entry_facility') return translations.timeEntryFacility
  return field.replaceAll('_', ' ')
}

function getLogActionWord(
  action: string,
  translations: WorkPeriodLogTranslations
) {
  const normalizedAction = normalizeAction(action)
  if (normalizedAction === 'CREATED') return translations.logActionCreated
  if (normalizedAction === 'UPDATED') return translations.logActionUpdated
  if (normalizedAction === 'DELETED') return translations.logActionDeleted
  if (normalizedAction === 'EXTENDED') return translations.logActionExtended
  return action.toLowerCase()
}

function getLogSummary(
  log: WorkPeriodLogNode,
  fields: ActionFieldRecord,
  translations: WorkPeriodLogTranslations
) {
  const actorName = log.user?.fullName || translations.system
  const actionWord = getLogActionWord(log.action, translations)

  // For span changes, focus the summary on "day length" rather than listing
  // the start/end fields separately.
  if ('extended_by' in fields) {
    return translations.logSummary({
      user: actorName,
      action: actionWord,
      target: translations.dayLength.toLowerCase(),
    })
  }

  const orderedFieldKeys = getOrderedFieldEntries(fields)
    .map(([field]) => field)
    // `source` is an attribution line, not a changed field — keep it out of
    // the summary's field list.
    .filter(field => field !== 'source')
  if (!orderedFieldKeys.length) {
    return translations.logSummary({
      user: actorName,
      action: actionWord,
      target: translations.workPeriod.toLowerCase(),
    })
  }

  const targets = orderedFieldKeys
    .map(field => getActionFieldLabel(field, translations).toLowerCase())
    .join(', ')

  return translations.logSummary({
    user: actorName,
    action: actionWord,
    target: targets,
  })
}

export function ViewWorkPeriodLogsModal({
  onClose,
  workPeriodId,
}: ViewWorkPeriodLogsModalProps) {
  const translations = useTranslate({
    actionLog: 'salary.work-period-log',
    noActivity: 'absence.details.log.no-activity',
    close: 'common.close',
    loading: 'common.loading',
    system: 'salary.log-system',
    start: 'common.start',
    end: 'common.end',
    approvedByUser: 'salary.log-approved-by-user',
    userComment: 'salary.log-user-comment',
    approvedByManager: 'salary.log-approved-by-manager',
    managerComment: 'salary.log-manager-comment',
    salarySetting: 'salary.log-salary-setting',
    leaderGroup: 'salary.log-leader-group',
    calendarItem: 'salary.log-calendar-item',
    dayLength: 'salary.log-day-length',
    yes: 'common.yes',
    no: 'common.no',
    workPeriod: 'salary.work-period',
    sourceActivity: 'salary.log-source-activity',
    sourceTimeEntry: 'salary.log-source-time-entry',
    sourceLabel: 'salary.log-source',
    activityPaid: 'salary.log-activity-paid',
    paid: 'common.paid',
    unpaid: 'common.unpaid',
    timeEntryStart: 'salary.log-time-entry-start',
    timeEntryEnd: 'salary.log-time-entry-end',
    timeEntryDepartment: 'salary.log-time-entry-department',
    timeEntryFacility: 'salary.log-time-entry-facility',
    hoursAbbrev: 'common.hours-abbrev',
    minutesAbbrev: 'common.minutes-abbrev',
    logSummary: [
      'absence.details.log.summary',
      { user: '', action: '', target: '' },
    ],
    extensionExtended: ['salary.log-extension-extended', { amount: '', source: '' }],
    extensionShortened: [
      'salary.log-extension-shortened',
      { amount: '', source: '' },
    ],
    extensionCreated: ['salary.log-extension-created', { amount: '', source: '' }],
    logActionCreated: 'absence.details.log.action-created',
    logActionUpdated: 'absence.details.log.action-updated',
    logActionDeleted: 'absence.details.log.action-deleted',
    logActionExtended: 'salary.log-action-extended',
    logShowDetails: 'absence.details.log.show-details',
    logHideDetails: 'absence.details.log.hide-details',
    logTabNew: 'absence.details.log.tab-new',
    logTabOld: 'absence.details.log.tab-old',
    lastLocked: ['salary.last-locked', { date: '' }],
    afterLock: 'salary.log-after-lock',
  }) as WorkPeriodLogTranslations

  const { data, loading, error } = useQuery(WORK_PERIOD_LOGS, {
    variables: { id: workPeriodId },
    fetchPolicy: 'cache-and-network',
  })

  const workPeriod = data?.workPeriod ?? null
  const logs = [...(workPeriod?.actionLogs ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  // Logs created after this moment are flagged as "after lock" in each row.
  const lockedAtMs = workPeriod?.lockedAt
    ? new Date(workPeriod.lockedAt).getTime()
    : null

  return (
    <RegularModal
      width="760px"
      cancelText={translations.close}
      onCancel={() => onClose(null)}
      cancelOnClickOutside
      overflowY="auto"
    >
      <div>
        <header className="mb-3 flex items-start justify-between gap-4 px-1">
          <h2 className="text-lg font-semibold text-stone-900">
            {translations.actionLog}
          </h2>
          <div className="flex items-center gap-3">
            {workPeriod?.lockedAt && (
              <span className="pt-0.5 text-sm text-stone-500">
                {translations.lastLocked({
                  date: format(
                    new Date(workPeriod.lockedAt),
                    'dd.MM.yyyy HH:mm'
                  ),
                })}
              </span>
            )}
            <button
              type="button"
              className="text-stone-400 transition-colors hover:text-stone-600"
              aria-label={translations.close}
              onClick={() => onClose(null)}
            >
              <FAIcon icon="times" />
            </button>
          </div>
        </header>
        <div className="max-h-[70vh] overflow-y-auto">
          {loading && !workPeriod ? (
            <p className="px-5 py-6 text-sm text-stone-600">
              {translations.loading}
            </p>
          ) : error ? (
            <p className="px-5 py-6 text-sm text-red-600">{error.message}</p>
          ) : (
            <ActionLogsList
              logs={logs}
              noActivityText={translations.noActivity}
              showDetailsText={translations.logShowDetails}
              hideDetailsText={translations.logHideDetails}
              oldTabLabel={translations.logTabOld}
              newTabLabel={translations.logTabNew}
              getLogSummary={(log, fields) =>
                getLogSummary(log, fields, translations)
              }
              getOrderedFieldEntries={getOrderedFieldEntries}
              getFieldLabel={field => getActionFieldLabel(field, translations)}
              formatFieldValue={(field, raw) =>
                formatActionFieldValue(field, raw, translations)
              }
              renderRowBadge={log =>
                lockedAtMs !== null &&
                new Date(log.createdAt).getTime() > lockedAtMs ? (
                  <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-amber-700">
                    {translations.afterLock}
                  </span>
                ) : null
              }
              rowClassName="py-3"
              emptyClassName="px-5 py-6 text-sm text-stone-500"
            />
          )}
        </div>
      </div>
    </RegularModal>
  )
}
